import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import marker2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

/**
 * Modal de trayectoria del contenedor — portado de `crearMapa`/`modalMapa` en
 * `IbuFront/.../ListadoContenedores.jsx` (V1). Dibuja 3 marcadores (POL, POD, posición
 * del buque) y una polilínea únicamente entre buque y POD (igual que V1: la polilínea
 * POL->buque está comentada/deshabilitada ahí, se replica tal cual).
 *
 * Fix del ícono default roto de Leaflet con bundlers ESM (Vite/Webpack): Leaflet
 * referencia sus imágenes vía rutas relativas que el bundler no resuelve; hay que
 * reasignar `L.Icon.Default.prototype._getIconUrl` con los assets importados.
 */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: marker2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Mismo candado que V1: solo se dibuja un punto si el par lat/long es numérico.
// Varios documentos reales tienen "Not Supported" en vesselLatitude/vesselLongitude
// (ShipsGo no siempre da la posición del buque) — parseFloat + isFinite lo filtra.
function coordsValidas(lat, lng) {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  return isFinite(la) && isFinite(lo) ? [la, lo] : null;
}

function diasParaLlegada(etaReal) {
  if (!etaReal) return null;
  const eta = new Date(etaReal);
  if (isNaN(eta.getTime())) return null;
  eta.setHours(0, 0, 0, 0);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.floor(
    (Date.UTC(eta.getFullYear(), eta.getMonth(), eta.getDate()) -
      Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())) /
      (1000 * 60 * 60 * 24)
  );
}

export default function MapaTrackingModal({ contenedor, onClose }) {
  const originCoords = useMemo(
    () => coordsValidas(contenedor?.polLatitude, contenedor?.polLongitude),
    [contenedor]
  );
  const destinationCoords = useMemo(
    () => coordsValidas(contenedor?.podLatitude, contenedor?.podLongitude),
    [contenedor]
  );
  const vesselCoords = useMemo(
    () => coordsValidas(contenedor?.vesselLatitude, contenedor?.vesselLongitude),
    [contenedor]
  );
  const dias = useMemo(() => diasParaLlegada(contenedor?.etaReal), [contenedor]);

  // Igual que V1: si no hay coordenadas del buque, se centra en el destino; si
  // tampoco hay destino, en el origen (V1 solo contemplaba el caso con buque, aquí
  // se agrega un respaldo razonable para no dejar el mapa en blanco).
  const centro = vesselCoords || destinationCoords || originCoords;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Trayectoria del Contenedor
            {dias !== null && ` — Faltan ${dias} día(s) para la llegada`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">
            Cerrar
          </button>
        </div>
        <div className="p-4">
          {centro ? (
            <MapContainer
              center={centro}
              zoom={3}
              style={{ height: '400px', width: '100%' }}
              maxBounds={[[85, -180], [-85, 180]]}
              maxBoundsViscosity={1.0}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                noWrap
              />
              {destinationCoords && <Marker position={destinationCoords} />}
              {vesselCoords && <Marker position={vesselCoords} />}
              {originCoords && <Marker position={originCoords} />}
              {vesselCoords && destinationCoords && (
                <Polyline positions={[vesselCoords, destinationCoords]} color="blue" dashArray="5,10" />
              )}
            </MapContainer>
          ) : (
            <p className="text-sm text-slate-500">No hay coordenadas disponibles para este contenedor.</p>
          )}
        </div>
      </div>
    </div>
  );
}
